import { useI18n } from '@ecom/i18n';
import { Button } from '@ecom/ui-kit';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactElement, useEffect, useRef, useState } from 'react';

import { disablePush, enablePush, pushEnabled, pushPublicKey, pushSupport } from '@/lib/push';
import { useSession } from '@/lib/session';

/**
 * The bell in the top bar — « Nouvelles commandes sur ce téléphone ».
 *
 * With phone notifications configured on the server (migration 0100), it opens a small panel to switch them on or
 * off for THIS device: on, a new order reaches the notification bar even with the dashboard closed. On an iPhone in
 * Safari it explains the one extra step Apple requires (add Souk to the home screen). Without server configuration
 * it falls back to the browser's own notifications while the dashboard is open (the earlier behaviour).
 */
export function PushBell(): ReactElement | null {
  const { t } = useI18n();
  const { accessToken, activeShopId } = useSession();
  const scope = { accessToken, shopId: activeShopId };
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const support = pushSupport();
  const ready = accessToken !== null && activeShopId !== null;

  const config = useQuery({
    queryKey: ['push-config', activeShopId],
    queryFn: async () => pushPublicKey(scope),
    enabled: ready,
    staleTime: Infinity,
  });
  const publicKey = config.data ?? null;
  const state = useQuery({
    queryKey: ['push-enabled', activeShopId],
    queryFn: async () => pushEnabled(scope),
    enabled: ready && publicKey !== null && support === 'supported',
  });
  const enabled = state.data === true;

  // The panel closes on Escape or a click outside it.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false);
    };
    const onClick = (event: MouseEvent): void => {
      if (panelRef.current !== null && !panelRef.current.contains(event.target as Node))
        setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [open]);

  const [legacyPermission, setLegacyPermission] = useState<NotificationPermission | 'unsupported'>(
    () => ('Notification' in window ? Notification.permission : 'unsupported'),
  );

  if (!ready || config.isPending) return null;

  // No phone notifications on this server: the browser's own, while the dashboard is open.
  if (publicKey === null) {
    if (legacyPermission !== 'default') return null;
    return (
      <BellButton
        label={t('merchant.newOrder.enableDesktop')}
        active={false}
        onClick={() => {
          void Notification.requestPermission().then(setLegacyPermission);
        }}
      />
    );
  }
  if (support === 'unsupported') return null;

  async function toggle(): Promise<void> {
    setBusy(true);
    setMessage(null);
    try {
      if (enabled) {
        await disablePush(scope);
        setMessage(t('merchant.push.disabled'));
      } else if (publicKey !== null) {
        const permission = await enablePush(scope, publicKey);
        setMessage(
          permission === 'granted' ? t('merchant.push.enabled') : t('merchant.push.blocked'),
        );
      }
    } catch {
      setMessage(t('merchant.push.failed'));
    } finally {
      setBusy(false);
      void queryClient.invalidateQueries({ queryKey: ['push-enabled', activeShopId] });
    }
  }

  return (
    <div ref={panelRef} className="relative">
      <BellButton
        label={t('merchant.push.title')}
        active={enabled}
        expanded={open}
        onClick={() => {
          setOpen((value) => !value);
          setMessage(null);
        }}
      />
      {open ? (
        <div
          role="dialog"
          aria-label={t('merchant.push.title')}
          className="souk-popover fixed inset-x-3 top-[68px] z-50 p-4 text-start sm:absolute sm:inset-x-auto sm:end-0 sm:top-[calc(100%+12px)] sm:w-80"
        >
          <p className="text-sm font-semibold text-ink">{t('merchant.push.title')}</p>
          {support === 'needs-install' ? (
            <div className="mt-2 flex flex-col gap-2 text-sm text-inkSoft">
              <p>{t('merchant.push.iosIntro')}</p>
              <ol className="list-decimal space-y-1 ps-5">
                <li>{t('merchant.push.iosStep1')}</li>
                <li>{t('merchant.push.iosStep2')}</li>
                <li>{t('merchant.push.iosStep3')}</li>
              </ol>
            </div>
          ) : (
            <>
              <p className="mt-1 text-sm text-inkSoft">
                {enabled ? t('merchant.push.onHint') : t('merchant.push.offHint')}
              </p>
              <Button
                className="mt-3 w-full"
                size="sm"
                variant={enabled ? 'secondary' : 'primary'}
                loading={busy}
                onClick={() => {
                  void toggle();
                }}
              >
                <span className="inline-flex items-center gap-2">
                  <BellIcon on={!enabled} className="h-4 w-4" />
                  {enabled ? t('merchant.push.turnOff') : t('merchant.push.turnOn')}
                </span>
              </Button>
            </>
          )}
          {message !== null ? (
            <p role="status" className="mt-2 text-xs text-inkSoft">
              {message}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** 🔔 notifications on for this device, 🔕 off — the state reads at a glance, without opening the panel. */
function BellIcon({ on, className }: { on: boolean; className: string }): ReactElement {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill={on ? 'currentColor' : 'none'}
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 8a6 6 0 1 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" fill="none" />
      {on ? null : <path d="m3 3 18 18" />}
    </svg>
  );
}

function BellButton({
  label,
  active,
  expanded,
  onClick,
}: {
  label: string;
  active: boolean;
  expanded?: boolean;
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      {...(expanded !== undefined ? { 'aria-expanded': expanded } : {})}
      // The Figma icon button; `aria-pressed` (notifications on) gives it the sage wash.
      className="sk-icon-btn"
      onClick={onClick}
    >
      <BellIcon on={active} className={`h-4 w-4 ${active ? 'bell-ring' : ''}`} />
    </button>
  );
}
